import { createSelector } from "@reduxjs/toolkit"
import PropTypes from "prop-types"
import { useMemo } from "react"
import { useSelector } from "react-redux"

import { fetchSize } from "areas/product/jewlr/modals/helper"
import {
  SizeBoxSmall,
  Size,
} from "areas/product/jewlr/modals/styled-components"
import MetalIcon from "areas/product/jewlr/wizard-options/choice/metal-icon"
import Image from "components/ui/image"
import { builderImage } from "helpers/product/builder-image"
import displayOptionDescription from "helpers/product/display-option-description"
import { imageLookup } from "helpers/product/image-lookup"
import { cn } from "styles/utils"

const getImage = (wizardData, pref, choices, selectedDiamond) => {
  if (!wizardData || wizardData.view_type == "no-image") {
    return null
  }

  const choiceData =
    pref === "diamond" ? choices.d_shape : choices[wizardData.pref]

  if (/c[0-9]+$/.test(wizardData.pref)) {
    return builderImage(wizardData.pref, choices)
  }

  if (wizardData.heading?.pref === "diamond") {
    if (selectedDiamond?.shape) {
      let shapeCode = selectedDiamond.shape.toLowerCase()
      let stoneType = selectedDiamond.stone_type
      return {
        x1: `${sl.config.jewlr_asset_url}images/product/diamond/${shapeCode}/${stoneType}.png`,
        x2: `${sl.config.jewlr_asset_url}images/product/diamond/${shapeCode}/${stoneType}_2x.png`,
      }
    }

    return {
      x1: `${sl.config.jewlr_asset_url}images/product/diamond/rd/04DIAM.png`,
      x2: `${sl.config.jewlr_asset_url}images/product/diamond/rd/04DIAM_2x.png`,
    }
  }
  if (typeof choiceData?.imageKey !== "undefined") {
    return imageLookup(wizardData.pref, choiceData.imageKey)
  }

  if (
    wizardData.pref == "ch" ||
    /ch[0-9]+$/.test(wizardData.pref) ||
    wizardData.pref == "box"
  ) {
    return null
  }

  if (typeof choiceData?.image !== "undefined") {
    return choiceData.image
  }

  return null
}

const selectWizardDataMap = (state) => state.wizard.data
const selectChoicesMap = (state) => state.choices
const selectDiamondSelector = (state) => state.diamondSelector
const selectDiamondUI = (state) => state.diamondUI

const makeSelectAccordionData = (pref) =>
  createSelector(
    [
      selectWizardDataMap,
      selectChoicesMap,
      selectDiamondSelector,
      selectDiamondUI,
    ],
    (wizardDataMap, choices, diamondSelector, diamondUI) => {
      const wizardData = wizardDataMap[pref]
      const wizardDataHeading = wizardData?.heading

      if (!wizardDataHeading) return null

      const selectedChoice = choices[pref]
      const dataRing = pref === "sz" ? wizardData : null
      const selectedMetal =
        pref === "sku" && wizardData && selectedChoice
          ? wizardData.options.find((opt) => opt.value === selectedChoice.value)
          : null

      const isRingSize = selectedChoice && dataRing && !dataRing.not_a_ring
      const sizeString =
        isRingSize && selectedChoice.value !== ""
          ? fetchSize(selectedChoice.option_description)
          : null

      return {
        bandColor: /b[0-9]+/.test(pref) ? choices[pref]?.rgb : null,
        dataRing,
        description: displayOptionDescription(
          wizardData,
          pref,
          choices,
          diamondSelector,
          diamondUI
        ),
        selectedChoice: choices[pref],
        selectedMetal,
        sizeString,
        thumbnailImage: getImage(
          wizardData,
          pref,
          choices,
          diamondSelector.selected_diamond
        ),
        wizardDataHeading,
      }
    }
  )

const AccordionThumbnail = ({ className, pref }) => {
  const selectAccordionData = useMemo(
    () => makeSelectAccordionData(pref),
    [pref]
  )
  const data = useSelector((state) => selectAccordionData(state))

  if (!data) return null

  const {
    bandColor,
    dataRing,
    description,
    selectedChoice,
    selectedMetal,
    sizeString,
    thumbnailImage,
    wizardDataHeading,
  } = data

  if (selectedChoice && dataRing && !dataRing.not_a_ring) {
    if (selectedChoice.value === "") {
      return (
        <SizeBoxSmall className={className} title="Size">
          <Size />
        </SizeBoxSmall>
      )
    }
    return (
      <SizeBoxSmall className={className} title="Size">
        <Size
          dangerouslySetInnerHTML={{
            __html: sizeString,
          }}
        />
      </SizeBoxSmall>
    )
  } else if (/b[0-9]+/.test(wizardDataHeading.pref)) {
    return (
      <div
        className={cn(
          "border-5 border-(--border-color) size-35 ml-7 mr-16 rounded-full",
          className
        )}
        style={{
          "--border-color": `rgb(${bandColor})`,
        }}
      />
    )
  } else if (
    (wizardDataHeading.pref === "sku" ||
      /m[0-9]+/.test(wizardDataHeading.pref)) &&
    selectedMetal
  ) {
    return (
      <MetalIcon
        className={cn(
          "text-14 tablet:m-0 tablet:mr-16 m-0 mr-16 size-40",
          className
        )}
        metal={selectedMetal}
      />
    )
  } else if (thumbnailImage && thumbnailImage.x2) {
    return (
      <Image
        alt={description || ""}
        className={cn("mr-16", className)}
        data-sizes="40px"
        height="40"
        key={`${pref}-thumbnail-image-object`}
        src={thumbnailImage.x2}
        srcSet={`${thumbnailImage.x1} 1x, ${thumbnailImage.x2} 2x`}
        title={description}
        width="40"
      />
    )
  } else {
    return (
      <Image
        alt={description || ""}
        className={cn("mr-16", className)}
        data-sizes="40px"
        height="40"
        key={`${pref}-thumbnail-image-string`}
        src={thumbnailImage || require("images/icons/jewlr.gif")}
        title={description}
        width="40"
      />
    )
  }
}

AccordionThumbnail.propTypes = {
  className: PropTypes.string,
  pref: PropTypes.string,
}

export default AccordionThumbnail
